import { useCallback, useEffect, useState } from 'react';
import { Loader2, MessageSquare, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { useNotesApi } from '@/services/api';
import { t } from '@/i18n';
import type { InvoiceNote } from '@ip/shared-types';

interface InvoiceNotesPanelProps {
  companyNip: string;
  invoiceId: string;
  /** Superadmin or company admin/ksiegowy — shows edit/delete per note. */
  canManage: boolean;
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('pl-PL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Task A3: accounting notes ("opisy dla ksiegowosci") on the invoice detail,
 * both directions. Append-only for regular users — the textarea + "Dodaj
 * opis" button is available to anyone who can see the invoice; edit/delete
 * buttons render only for admins/ksiegowi (the backend enforces the same
 * grade, this is just honest UI). Author + timestamp come from the server.
 */
export function InvoiceNotesPanel({ companyNip, invoiceId, canManage }: InvoiceNotesPanelProps) {
  const [notes, setNotes] = useState<InvoiceNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [newBody, setNewBody] = useState('');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<InvoiceNote | null>(null);
  const [deleting, setDeleting] = useState(false);

  const { listNotes, addNote, updateNote, deleteNote } = useNotesApi();

  const loadNotes = useCallback(async () => {
    try {
      setLoading(true);
      const data = await listNotes(companyNip, invoiceId);
      setNotes(data);
    } catch (error) {
      console.error('Failed to load notes:', error);
    } finally {
      setLoading(false);
    }
  }, [companyNip, invoiceId, listNotes]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const handleAdd = async () => {
    const body = newBody.trim();
    if (!body) return;
    try {
      setAdding(true);
      const note = await addNote(companyNip, invoiceId, body);
      setNotes((prev) => [...prev, note]);
      setNewBody('');
      toast.success(t('invoices.notes.addSuccess'));
    } catch (error) {
      toast.error(t('invoices.notes.addError'));
      console.error(error);
    } finally {
      setAdding(false);
    }
  };

  const handleSaveEdit = async () => {
    const body = editBody.trim();
    if (!editingId || !body) return;
    try {
      setSavingEdit(true);
      const updated = await updateNote(companyNip, invoiceId, editingId, body);
      setNotes((prev) => prev.map((n) => (n.id === updated.id ? updated : n)));
      setEditingId(null);
      setEditBody('');
      toast.success(t('invoices.notes.updateSuccess'));
    } catch (error) {
      toast.error(t('invoices.notes.updateError'));
      console.error(error);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await deleteNote(companyNip, invoiceId, deleteTarget.id);
      setNotes((prev) => prev.filter((n) => n.id !== deleteTarget.id));
      toast.success(t('invoices.notes.deleteSuccess'));
    } catch (error) {
      toast.error(t('invoices.notes.deleteError'));
      console.error(error);
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  return (
    <Card className="gap-3 py-4">
      <CardHeader className="px-4">
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <MessageSquare className="size-4" />
          <span>{t('invoices.notes.title')}</span>
          {notes.length > 0 && (
            <span className="text-muted-foreground text-xs font-normal">({notes.length})</span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 px-4">
        {loading ? (
          <Skeleton className="h-12 w-full" />
        ) : notes.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            {t('invoices.notes.empty')}{' '}
            <span className="text-xs">— {t('invoices.notes.emptyHint')}</span>
          </p>
        ) : (
          <div className="space-y-2">
            {notes.map((note) => (
              <div key={note.id} className="bg-muted/40 rounded-md border px-3 py-2">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-muted-foreground text-xs">
                    <span className="text-foreground font-medium">{note.createdByName}</span>
                    {' · '}
                    <span className="tabular-nums">{formatDateTime(note.createdAt)}</span>
                    {note.editedAt && (
                      <span className="italic">
                        {' · '}
                        {note.editedByName
                          ? t('invoices.notes.editedBy', {
                              date: formatDateTime(note.editedAt),
                              name: note.editedByName,
                            })
                          : t('invoices.notes.edited')}
                      </span>
                    )}
                  </p>
                  {canManage && editingId !== note.id && (
                    <div className="flex shrink-0 items-center gap-0.5">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-6"
                        aria-label={t('common.edit')}
                        onClick={() => {
                          setEditingId(note.id);
                          setEditBody(note.body);
                        }}
                      >
                        <Pencil className="size-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive size-6"
                        aria-label={t('common.delete')}
                        onClick={() => setDeleteTarget(note)}
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
                {editingId === note.id ? (
                  <div className="mt-2 space-y-2">
                    <Textarea
                      value={editBody}
                      onChange={(e) => setEditBody(e.target.value)}
                      rows={3}
                      maxLength={5000}
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={savingEdit}
                        onClick={() => {
                          setEditingId(null);
                          setEditBody('');
                        }}
                      >
                        {t('invoices.notes.cancelEdit')}
                      </Button>
                      <Button size="sm" disabled={!editBody.trim() || savingEdit} onClick={handleSaveEdit}>
                        {savingEdit && <Loader2 className="size-4 animate-spin" />}
                        {t('invoices.notes.saveEdit')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="mt-1 text-sm whitespace-pre-wrap">{note.body}</p>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="space-y-2">
          <Textarea
            placeholder={t('invoices.notes.placeholder')}
            value={newBody}
            onChange={(e) => setNewBody(e.target.value)}
            rows={2}
            maxLength={5000}
          />
          <div className="flex justify-end">
            <Button size="sm" disabled={!newBody.trim() || adding} onClick={handleAdd}>
              {adding && <Loader2 className="size-4 animate-spin" />}
              {t('invoices.notes.addButton')}
            </Button>
          </div>
        </div>
      </CardContent>

      <AlertDialog open={deleteTarget !== null} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('invoices.notes.deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('invoices.notes.deleteConfirmMessage')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              disabled={deleting}
              onClick={handleDelete}
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
